import React from 'react';
import PropTypes from 'prop-types';
import ContextMenu from 'relements/build/esm/overlays/contextMenu';
import RelIcon from 'relements/build/esm/ui/icon';
import Tooltip from 'relements/build/esm/overlays/tooltip';

import { GRAPH_NODE_DISPLAY_TEXT } from '@src/constants/MogamboConstants';

import ReorderIcon from '../../assets/ReorderIcon.svg';
import MoreIcon from '../../assets/More.svg';
import BulbIcon from '../../assets/Bulb.svg';
import TickCircle from './icons/TickCircle.svg';
import CrossCircle from './icons/CrossCircle.svg';

import styles from './Label.scss';

/**
 * A presentational list item style component used to render a list item as a
 * part of either a list of substories or a list of nodes. Renders a colored icon
 * as well as the text.
 */
const Label = (props) => {
  /**
   * inputMode tracks whether to show the label or the input
   * inputText is the text that the user has entered into the input
   * @type {Object}
   */
  const [dropMode, setDropMode] = React.useState(false);
  const [inputText, setInputText] = React.useState(props.title);
  const [contextMenuActive, setContextMenuActive] = React.useState('');
  const [contextMenuIcon, setContextMenuIcon] = React.useState('');
  const { inputMode, smartSkillsEnabled, closeAddSkillDrawer } = props;

  /**
   * Label rendered
   * @return {[type]} [description]
   */
  const _renderLabel = () => {
    const {
      children,
      small,
      color,
      type,
      onClick,
      onDragStart,
      onDragEnd,
      disabled,
      active,
      draggable,
      innerRef,
      customCSS,
      isSmartSkill,
    } = props;

    if (type === GRAPH_NODE_DISPLAY_TEXT.AB_NODE) return null;

    /**
     * smallClassName tracks whether the label is smaller in size (when a part
     * of list of nodes) or normal (when a part of list of substories)
     *
     * disabledClassName tracks whether the node is inactive or not,
     * (inactive nodes are faded and not colored)
     */
    const smallClassName = small ? styles.small : '';
    const disabledClassName = disabled ? styles.disabled : '';
    const dragDisabledClassName = !draggable ? styles.dragDisabled : '';
    const activeClassName = active ? styles.active : '';
    const dropModeClassName = dropMode ? styles.dropMode : '';
    const contextMenuActiveClassName = contextMenuActive
      ? styles.contextMenuActive
      : '';
    const noLeftPadding = customCSS ? styles.noLeftPadding : '';

    let nodeTypeClassName = '';

    if (type === GRAPH_NODE_DISPLAY_TEXT.STATIC) {
      nodeTypeClassName = styles.static;
    } else if (type === GRAPH_NODE_DISPLAY_TEXT.OUTPUT) {
      nodeTypeClassName = styles.output;
    } else if (type === GRAPH_NODE_DISPLAY_TEXT.CODE) {
      nodeTypeClassName = styles.small;
    }

    return (
      <div
        className={`${styles.substoryLabel} ${smallClassName} ${disabledClassName} ${activeClassName} ${dropModeClassName} ${noLeftPadding}`}
        draggable={draggable}
        onClick={!active ? onClick : undefined}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDrop={_handleOnDrop}
        onDragOver={_handleDragOver}
        onDragLeave={_handleDragLeave}
        ref={(ref) => {
          innerRef && innerRef(ref);
        }}
        data-testid="nodeItemSmartSkills"
      >
        {draggable ? (
          <RelIcon
            src={ReorderIcon}
            onClick={_openContextMenu}
            className={`${styles.reorderIcon} ${dragDisabledClassName}`}
          />
        ) : null}
        <div
          style={{ backgroundColor: color }}
          className={`${styles.substoryLabelIcon} ${nodeTypeClassName}`}
        >
          {isSmartSkill && (
            <Tooltip themed tooltip="Smart Skill">
              <RelIcon src={BulbIcon} className={styles.smartSkillIcon} />
            </Tooltip>
          )}
        </div>
        <div
          className={`${styles.substoryLabelText} ${smallClassName} ${
            active && styles.substoryLabelTextActive
          }`}
        >
          {children || <span style={{ opacity: 0.4 }}>Name your node</span>}
        </div>
        {props.contextMenuItems.length ? (
          <div className={styles.substoryCTAs}>
            <RelIcon
              onClick={_openContextMenu}
              src={MoreIcon}
              className={`${styles.substoryCTA} ${contextMenuActiveClassName}`}
              innerRef={(DOMElement) => {
                setContextMenuIcon(DOMElement);
              }}
            />
            <ContextMenu
              onClose={_closeContextMenu}
              active={contextMenuActive}
              attachTo={contextMenuIcon}
              offset={{ left: 4, top: -6 }}
              prefixClassName="substoryLabelContextMenu"
            >
              {_renderContextMenuItems()}
            </ContextMenu>
          </div>
        ) : null}
      </div>
    );
  };

  const _renderInput = () => {
    return (
      <div className={`${styles.addLabelInputWrapper}`}>
        <input
          type="text"
          value={inputText}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setInputText(e.target.value)}
          className={`${styles.addLabelInput}`}
          placeholder={props.placeholder}
        />
        <div className={`${styles.addLabelInputIcons}`}>
          <div
            onClick={(e) => _onAdd(e)}
            className={styles.addLabelInputIconWrapper}
          >
            <RelIcon src={TickCircle} className={styles.addLabelInputIcon} />
          </div>
          <div
            className={styles.addLabelInputIconWrapper}
            onClick={(e) => _switchToLabel(e)}
          >
            <RelIcon src={CrossCircle} className={styles.addLabelInputIcon} />
          </div>
        </div>
      </div>
    );
  };

  const _renderContextMenuItems = () => {
    return props.contextMenuItems.map((contextMenuItem, i) => {
      return (
        <ContextMenu.Item
          key={i}
          title={contextMenuItem.title}
          icon={contextMenuItem.icon}
          onClick={_handleContextMenuClick(contextMenuItem)}
          iconSize={18}
          disabled={contextMenuItem.disabled}
          prefixClassName="substoryLabelContextMenuItem"
        />
      );
    });
  };

  /**
   * Opens the context menu for a particular label
   * @return {[type]} [description]
   */
  const _openContextMenu = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setContextMenuActive(true);
    closeAddSkillDrawer();
  };

  /**
   * Closes the context menu for a particular label
   * @return {[type]} [description]
   */
  const _closeContextMenu = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setContextMenuActive(false);
  };

  const _handleContextMenuClick = (contextMenuItem) => (e) => {
    e.stopPropagation();
    if (contextMenuItem._internalFunction) {
      const functionName = contextMenuItem._internalFunction;
      if (functionName === '_switchToInput') {
        _switchToInput(e);
      }
    } else {
      contextMenuItem.onClick(e);
    }

    _closeContextMenu();
  };

  const _handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    closeAddSkillDrawer();
    if (props.dropActive) {
      setDropMode(true);
    }
  };

  const _handleDragLeave = () => {
    setDropMode(false);
  };

  const _handleOnDrop = (e) => {
    if (props.dropActive) {
      setDropMode(false);
      props.onDrop(e);
    }
  };

  const _onAdd = (e) => {
    e.stopPropagation();
    props.onRename(inputText);
    _switchToLabel(e);
  };

  const _switchToInput = (e) => {
    e.stopPropagation();
    props.setInputMode(true);
  };

  const _switchToLabel = (e) => {
    e.stopPropagation();
    setInputText(props.title === inputText ? props.title : inputText);
    props.setInputMode(false);
  };

  /**
   * based on the inputMode state, we either render the label or the input
   * @return {[type]} [description]
   */
  return inputMode ? _renderInput() : _renderLabel();
};

Label.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node.isRequired,
  color: PropTypes.string,
  contextMenuItems: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      icon: PropTypes.func,
    })
  ),
  disabled: PropTypes.bool,
  draggable: PropTypes.bool,
  customCSS: PropTypes.bool,
  dropActive: PropTypes.bool,
  onClick: PropTypes.func,
  onDragEnd: PropTypes.func,
  onDragStart: PropTypes.func,
  onDrop: PropTypes.func,
  onRename: PropTypes.func,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  small: PropTypes.bool,
  title: PropTypes.string,
  innerRef: PropTypes.func,
};

export default Label;
